import { useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from 'src/context/ToastContext';

export type InboundEmailWorkflowsModalProps = {
  inboundEmailId: string;
  onClose: () => void;
};

export function InboundEmailWorkflowsModal({
  inboundEmailId,
  onClose,
}: InboundEmailWorkflowsModalProps) {
  const { showToast } = useToast();
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);

  const previewQuery = useQuery({
    ...orpc.communications.emailRules.previewInboundEmailWorkflows.queryOptions(
      {
        input: { inboundEmailId },
      },
    ),
  });

  const rerunMutation = useMutation(
    orpc.communications.emailRules.rerunInboundEmailWorkflows.mutationOptions(),
  );

  const workflows = previewQuery.data?.workflows ?? [];
  const matchingRules = previewQuery.data?.matchingRules ?? [];

  const allEventOptions = useMemo(() => {
    const rules = previewQuery.data?.matchingRules ?? [];
    const events: Array<{
      id: string;
      name: string | null;
      ruleName: string;
    }> = [];
    for (const rule of rules) {
      for (const event of rule.events) {
        events.push({
          id: event.id,
          name: event.name,
          ruleName: rule.ruleName,
        });
      }
    }
    return events;
  }, [previewQuery.data?.matchingRules]);

  const hasMultipleEvents = allEventOptions.length > 1;

  const handleEventToggle = (eventId: string, checked: boolean) => {
    if (checked) {
      setSelectedEventIds((prev) => [...prev, eventId]);
    } else {
      setSelectedEventIds((prev) => prev.filter((id) => id !== eventId));
    }
  };

  const handleSelectAll = () => {
    if (selectedEventIds.length === allEventOptions.length) {
      setSelectedEventIds([]);
    } else {
      setSelectedEventIds(allEventOptions.map((e) => e.id));
    }
  };

  async function handleRerunWorkflows() {
    try {
      const emailRuleOptionIds =
        hasMultipleEvents && selectedEventIds.length > 0
          ? selectedEventIds
          : undefined;

      await rerunMutation.mutateAsync({
        inboundEmailId,
        emailRuleOptionIds,
      });

      const triggeredEvents =
        hasMultipleEvents && selectedEventIds.length > 0
          ? allEventOptions.filter((e) => selectedEventIds.includes(e.id))
          : allEventOptions;

      const eventNames = triggeredEvents
        .map((event) => `"${event.name}"`)
        .join(', ');

      showToast({
        title: 'Email reprocessed',
        message: eventNames
          ? `Triggered ${eventNames}`
          : 'Email reprocessed successfully',
        color: 'success',
        icon: 'check_circle',
      });

      previewQuery.refetch();
      setSelectedEventIds([]);
    } catch (error) {
      showToast({
        title: 'Error reprocessing email',
        message: error instanceof Error ? error.message : String(error),
        color: 'error',
        icon: 'error',
      });
    }
  }

  const canRerun =
    matchingRules.length > 0 &&
    (!hasMultipleEvents || selectedEventIds.length > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Email Rule Workflows</DialogTitle>
          {hasMultipleEvents && (
            <DialogDescription>
              Select which events to trigger when re-running.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {previewQuery.isLoading ? (
            <div className="flex justify-center p-6">
              <CircleNotchIcon className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : previewQuery.isError ? (
            <p className="text-sm text-destructive">
              {previewQuery.error instanceof Error
                ? previewQuery.error.message
                : 'Failed to load workflows'}
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">Matching Email Rules</h3>
                  {hasMultipleEvents && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                      className="h-auto py-1 px-2 text-xs"
                    >
                      {selectedEventIds.length === allEventOptions.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </Button>
                  )}
                </div>
                {matchingRules.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No rules matched.
                  </p>
                ) : hasMultipleEvents ? (
                  <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-3">
                    {allEventOptions.map((event) => (
                      <div
                        key={event.id}
                        className="flex items-start space-x-2"
                      >
                        <Checkbox
                          id={`event-${event.id}`}
                          checked={selectedEventIds.includes(event.id)}
                          onCheckedChange={(checked) =>
                            handleEventToggle(event.id, checked === true)
                          }
                        />
                        <div className="grid gap-0.5 leading-none">
                          <Label
                            htmlFor={`event-${event.id}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {event.name ?? 'Unnamed Event'}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Rule: {event.ruleName}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {matchingRules.map((rule) => (
                      <li key={rule.id} className="text-sm">
                        {rule.ruleName}
                        {rule.events.length > 0 && (
                          <span className="text-muted-foreground">
                            {' '}
                            (
                            {rule.events
                              .map((e) => e.name ?? 'Unnamed')
                              .join(', ')}
                            )
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Triggerable Workflows</h3>
                {workflows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No workflows would run for this email.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {workflows.map((wf) => (
                      <li key={wf.id} className="text-sm">
                        {wf.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleRerunWorkflows}
            disabled={rerunMutation.isPending || !canRerun}
          >
            {rerunMutation.isPending ? 'Re-running…' : 'Re-run ingestion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
